import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DwFilterType, DwMetaDataService, DwMetaDataServiceToken, DwQueryFilter, DwQueryFilterGroup, DwQueryHelper, DwQueryParam } from '@devwareapps/devware-cap';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ActiveFilter } from '../../models/active-filter.model';
import { FilterConfig } from '../../models/filter-config.model';
import { FilterType } from '../../models/filter-type.enum';
import { ensureArray } from '../../../shared/util/ensure-array.util';
import { AdminManagerService } from '../../services/admin-manager.service';
import { LookupDisplayResolverService } from '../../services/lookup-display-resolver.service';

@Component({
  selector: 'app-generic-tag-filters',
  templateUrl: './generic-tag-filters.component.html',
  styleUrls: ['./generic-tag-filters.component.scss']
})
export class GenericTagFiltersComponent implements OnInit, OnChanges {
  @Input() filterConfigs: FilterConfig[] = [];
  @Input() autoRefresh = true;
  /** A saved/native CAP filter group to display as editable filter tags. */
  @Input() appliedFilters: DwQueryFilterGroup;
  @Input() appliedFilterState: any[] = [];
  /** Reapply configured filter defaults when the supplied group is empty. */
  @Input() applyConfiguredDefaultsWhenEmpty = true;
  @Output() filtersChanged = new EventEmitter<DwQueryFilterGroup>();
  @Output() customFilterStateChanged = new EventEmitter<any[]>();
  /** Emitted only for an explicit add, edit, remove, or clear action. */
  @Output() filtersEdited = new EventEmitter<void>();

  activeFilters: ActiveFilter[] = [];
  availableFilters: FilterConfig[] = [];
  showFilterPanel = false;
  selectedFilterConfig: FilterConfig = null;
  filterSearchText = '';
  lookupOptionSearchText = '';
  lookupOptions: any[] = [];
  lookupOptionsLoading = false;
  lookupOptionsError: string;

  FilterType = FilterType;

  // Temporary values for filter controls
  tempFilterValue: any = null;
  tempDateRangeStart: Date | string = null;
  tempDateRangeEnd: Date | string = null;
  tempReportDateMode = 'rollingDays';
  tempReportDateDays = 90;

  private lookupContext: any;
  private initialized = false;
  private appliedFilterGeneration = 0;

  get filteredAvailableFilters(): FilterConfig[] {
    const searchText = (this.filterSearchText || '').trim().toLowerCase();
    return searchText
      ? this.availableFilters.filter(config => (config.label || '').toLowerCase().indexOf(searchText) >= 0)
      : this.availableFilters;
  }

  get filteredLookupOptions(): any[] {
    const searchText = (this.lookupOptionSearchText || '').trim().toLowerCase();
    return searchText
      ? this.lookupOptions.filter(option => (option.Display || '').toLowerCase().indexOf(searchText) >= 0)
      : this.lookupOptions;
  }

  constructor(
    private adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
    private lookupDisplayResolver: LookupDisplayResolverService
  ) { }

  ngOnInit(): void {
    this.lookupContext = this.adminManagerService.getPilexosUser() || {};
    this.initialized = true;

    this.updateAvailableFilters();
    if (this.appliedFilters?.Filters?.length || !this.applyConfiguredDefaultsWhenEmpty) {
      this.applyFilterGroup(this.appliedFilters || new DwQueryFilterGroup());
    } else {
      this.applyConfiguredDefaultFilters();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized
      || (!changes.appliedFilters
        && !changes.filterConfigs
        && !changes.applyConfiguredDefaultsWhenEmpty)) {
      return;
    }

    // appliedFilterState is companion data for appliedFilters. Changes to it
    // are also emitted by this component while users edit filters, so treating
    // it as a standalone apply command would restore the stale preset group.
    if (this.appliedFilters?.Filters?.length || !this.applyConfiguredDefaultsWhenEmpty) {
      this.applyFilterGroup(this.appliedFilters || new DwQueryFilterGroup());
    } else {
      this.applyConfiguredDefaultFilters();
    }
  }

  /**
   * Opens the filter selection dropdown panel
   */
  openFilterPanel(): void {
    this.showFilterPanel = true;
  }

  /**
   * Closes the filter panel and resets selection
   */
  closeFilterPanel(): void {
    this.showFilterPanel = false;
    this.selectedFilterConfig = null;
    this.filterSearchText = '';
    this.lookupOptionSearchText = '';
    this.lookupOptions = [];
    this.lookupOptionsLoading = false;
    this.lookupOptionsError = null;
    this.resetTempValues();
  }

  /**
   * Called when user selects a filter from the available filters list
   */
  onFilterSelected(config: FilterConfig): void {
    this.selectedFilterConfig = config;
    this.tempFilterValue = config.defaultValue;
    this.lookupOptionSearchText = '';
    this.lookupOptions = [];
    this.lookupOptionsError = null;
    if (config.type === FilterType.MultiSelect && !Array.isArray(this.tempFilterValue)) {
      this.tempFilterValue = this.tempFilterValue === null || this.tempFilterValue === undefined
        ? []
        : [this.tempFilterValue];
    }
    if (config.type === FilterType.ReportDatePeriod) {
      const value = config.defaultValue || { mode: 'rollingDays', days: 90 };
      this.tempReportDateMode = value.mode;
      this.tempReportDateDays = value.days || 90;
      this.tempDateRangeStart = value.start ? this.toLocalDate(value.start) : null;
      this.tempDateRangeEnd = value.end ? this.toLocalDate(value.end) : null;
    }
    
    if (config.type === FilterType.Toggle) {
      this.tempFilterValue = this.tempFilterValue !== undefined ? this.tempFilterValue : true;
    }

    if (config.lookupId
      && (config.type === FilterType.Dropdown || config.type === FilterType.MultiSelect)) {
      this.loadLookupOptions(config);
    }
  }

  isLookupOptionSelected(option: any): boolean {
    return ensureArray(this.tempFilterValue)
      .some(value => `${value}` === `${option?.Id}`);
  }

  selectLookupOption(option: any): void {
    if (!this.selectedFilterConfig || option?.Id === undefined || option?.Id === null) {
      return;
    }
    if (this.selectedFilterConfig.type === FilterType.Dropdown) {
      this.tempFilterValue = option.Id;
      return;
    }

    const selectedValues = ensureArray(this.tempFilterValue);
    const selectedIndex = selectedValues.findIndex(value => `${value}` === `${option.Id}`);
    this.tempFilterValue = selectedIndex >= 0
      ? selectedValues.filter((_value, index) => index !== selectedIndex)
      : [...selectedValues, option.Id];
  }

  /**
   * Applies the selected filter and adds it to active filters
   */
  applySelectedFilter(): void {
    if (!this.selectedFilterConfig) {
      return;
    }

    let value: any;
    let displayValue = '';

    switch (this.selectedFilterConfig.type) {
      case FilterType.DateRange:
        if (!this.tempDateRangeStart || !this.tempDateRangeEnd) {
          return; // Don't apply incomplete date range
        }
        value = {
          start: this.tempDateRangeStart,
          end: this.tempDateRangeEnd
        };
        displayValue = `${this.formatDate(this.tempDateRangeStart)} - ${this.formatDate(this.tempDateRangeEnd)}`;
        break;

      case FilterType.DatePicker:
        value = this.tempFilterValue;
        displayValue = this.formatDate(this.tempFilterValue);
        break;

      case FilterType.ReportDatePeriod:
        if (this.tempReportDateMode === 'exactRange') {
          if (!this.tempDateRangeStart || !this.tempDateRangeEnd) {
            return;
          }
          value = {
            mode: 'exactRange',
            start: this.toDateValue(this.tempDateRangeStart),
            end: this.toDateValue(this.tempDateRangeEnd)
          };
        } else if (this.tempReportDateMode === 'rollingDays') {
          if (!this.tempReportDateDays || this.tempReportDateDays < 1) {
            return;
          }
          value = { mode: 'rollingDays', days: this.tempReportDateDays };
        } else {
          value = { mode: this.tempReportDateMode };
        }
        displayValue = this.reportDatePeriodDisplay(value);
        break;

      case FilterType.Toggle:
        value = this.tempFilterValue;
        displayValue = value ? 'Yes' : 'No';
        break;

      case FilterType.Dropdown:
      case FilterType.MultiSelect:
        value = this.tempFilterValue;
        if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
          return;
        }

        // If predefined options exist, use their labels synchronously
        if (this.selectedFilterConfig.options) {
          displayValue = this.getDisplayValueForDropdown(value, this.selectedFilterConfig);
          break;
        }

        // Lookup options were already loaded for the selector. Reuse them when
        // possible; the shared resolver handles saved values and partial lists.
        if (this.selectedFilterConfig.lookupId) {
          const selectedConfig = this.selectedFilterConfig;
          const loadedOptions = this.lookupOptions;
          this.resolveLookupDisplayValue(selectedConfig, value, loadedOptions).subscribe(resolvedDisplay => {
            if (this.selectedFilterConfig !== selectedConfig) {
              return;
            }
            const existingIndex2 = this.activeFilters.findIndex(f => f.field === selectedConfig.field);
            if (existingIndex2 >= 0) {
              this.activeFilters[existingIndex2] = {
                field: selectedConfig.field,
                value: value,
                displayLabel: selectedConfig.label,
                displayValue: resolvedDisplay
              };
            } else {
              this.activeFilters.push({
                field: selectedConfig.field,
                value: value,
                displayLabel: selectedConfig.label,
                displayValue: resolvedDisplay
              });
            }

            this.closeFilterPanel();
            this.updateAvailableFilters();
            this.emitFiltersChanged(true);
          });

          // Will be applied in async callback
          return;
        }

        // Fallback: show raw values
        displayValue = ensureArray(value).join(', ');
        break;

      case FilterType.TextInput:
        value = this.tempFilterValue;
        displayValue = value;
        break;

      default:
        value = this.tempFilterValue;
        displayValue = value?.toString() || '';
    }

    if (value === null || value === undefined || value === '') {
      return;
    }

    // Check if filter already exists and update it
    const existingIndex = this.activeFilters.findIndex(f => f.field === this.selectedFilterConfig.field);
    if (existingIndex >= 0) {
      this.activeFilters[existingIndex] = {
        field: this.selectedFilterConfig.field,
        value: value,
        displayLabel: this.selectedFilterConfig.label,
        displayValue: displayValue
      };
    } else {
      this.activeFilters.push({
        field: this.selectedFilterConfig.field,
        value: value,
        displayLabel: this.selectedFilterConfig.label,
        displayValue: displayValue
      });
    }

    this.closeFilterPanel();
    this.updateAvailableFilters();
    this.emitFiltersChanged(true);
  }

  /**
   * Removes an active filter
   */
  removeFilter(filter: ActiveFilter): void {
    this.activeFilters = this.activeFilters.filter(f => f.field !== filter.field);
    this.updateAvailableFilters();
    this.emitFiltersChanged(true);
  }

  /**
   * Clears all active filters
   */
  clearAllFilters(): void {
    this.activeFilters = [];
    this.updateAvailableFilters();
    this.emitFiltersChanged(true);
  }

  /**
   * Updates the list of available filters (excluding already active ones)
   */
  private updateAvailableFilters(): void {
    const activeFields = this.activeFilters.map(f => f.field);
    this.availableFilters = this.filterConfigs.filter(config => !activeFields.includes(config.field));
  }

  /**
   * Builds and emits the DwQueryFilterGroup based on active filters
   */
  private emitFiltersChanged(userEdited = false): void {
    if (userEdited) {
      // A late default lookup must not replace a selection the user just changed or cleared.
      ++this.appliedFilterGeneration;
    }
    const filterGroup = new DwQueryFilterGroup();
    const qh = new DwQueryHelper();

    this.activeFilters.forEach(activeFilter => {
      const config = this.filterConfigs.find(c => c.field === activeFilter.field);
      if (!config || !config.attribute) {
        return;
      }

      const attrName = typeof config.attribute === 'string' 
        ? config.attribute 
        : config.attribute.fieldNameWithItemPrefix;

      switch (config.type) {
        case FilterType.DateRange:
          if (activeFilter.value.start && activeFilter.value.end) {
            filterGroup.AddFilterAnd(qh.filters.GreaterThanEqual(attrName, this.toLocalDate(activeFilter.value.start)));
            filterGroup.AddFilterAnd(qh.filters.LessThanEqual(attrName, this.toLocalDate(activeFilter.value.end)));
          }
          break;

        case FilterType.ReportDatePeriod:
          filterGroup.AddFilterAnd(this.buildReportDatePeriodFilter(qh, config, activeFilter.value));
          break;

        case FilterType.DatePicker:
          filterGroup.AddFilterAnd(this.buildConfiguredFilter(qh, config, attrName, activeFilter.value, DwFilterType.Equal));
          break;

        case FilterType.Toggle:
          filterGroup.AddFilterAnd(this.buildConfiguredFilter(qh, config, attrName, activeFilter.value, DwFilterType.Equal));
          break;

        case FilterType.Dropdown:
        case FilterType.MultiSelect:
          const values = ensureArray(activeFilter.value);
          filterGroup.AddFilterAnd(this.buildConfiguredFilter(qh, config, attrName, values, DwFilterType.In));
          break;

        case FilterType.TextInput:
          filterGroup.AddFilterAnd(this.buildConfiguredFilter(qh, config, attrName, activeFilter.value, DwFilterType.Contains));
          break;
      }
    });

    this.customFilterStateChanged.emit(this.activeFilters
      .filter(active => this.filterConfigs.find(config => config.field === active.field)?.filterTypeKey)
      .map(active => {
        const config = this.filterConfigs.find(item => item.field === active.field);
        return {
          filterKey: config.field,
          filterTypeKey: config.filterTypeKey,
          attribute: typeof config.attribute === 'string' ? config.attribute : config.attribute.fieldNameWithItemPrefix,
          value: active.value
        };
      }));
    this.filtersChanged.emit(filterGroup);
    if (userEdited) {
      this.filtersEdited.emit();
    }
  }

  private loadLookupOptions(config: FilterConfig): void {
    this.lookupOptionsLoading = true;
    this.getLookupQueryParams(config).pipe(
      switchMap(queryParams => this.dwMetaDataService.getLookup(config.lookupId, queryParams)),
      take(1)
    )
      .subscribe(options => {
        if (this.selectedFilterConfig !== config) {
          return;
        }
        this.lookupOptions = options || [];
        this.lookupOptionsLoading = false;
      }, error => {
        if (this.selectedFilterConfig !== config) {
          return;
        }
        this.lookupOptionsLoading = false;
        this.lookupOptionsError = error?.message || error || 'Lookup values could not be loaded.';
      });
  }

  private applyFilterGroup(group: DwQueryFilterGroup): void {
    const generation = ++this.appliedFilterGeneration;
    const leaves = this.flattenFilters(group);
    const next: ActiveFilter[] = [];

    this.filterConfigs.forEach(config => {
      const attrName = typeof config.attribute === 'string'
        ? config.attribute
        : config.attribute?.fieldNameWithItemPrefix;
      const savedCustomState = (this.appliedFilterState || []).find(state => state.attribute === attrName);
      if (config.type === FilterType.ReportDatePeriod && savedCustomState) {
        next.push({
          field: config.field,
          value: savedCustomState.value,
          displayLabel: config.label,
          displayValue: this.reportDatePeriodDisplay(savedCustomState.value)
        });
        return;
      }
      const matching = leaves.filter(filter => filter.Attr === attrName);
      if (!matching.length) {
        return;
      }

      const active = this.toActiveFilter(config, matching);
      if (active) {
        next.push(active);
        if ((config.type === FilterType.Dropdown || config.type === FilterType.MultiSelect) && config.lookupId) {
          active.displayValue = '';
          active.displayLoading = true;
          this.resolveLookupDisplayValue(config, active.value).subscribe(display => {
            if (generation === this.appliedFilterGeneration) {
              active.displayValue = display;
              active.displayLoading = false;
            }
          }, error => {
            console.warn(`Lookup display could not be resolved for ${config.lookupId}.`, error);
            if (generation === this.appliedFilterGeneration) {
              active.displayValue = ensureArray(active.value).join(', ');
              active.displayLoading = false;
            }
          });
        }
      }
    });

    this.activeFilters = next;
    this.updateAvailableFilters();
  }

  private flattenFilters(filter: DwQueryFilter): DwQueryFilter[] {
    const children = (filter as DwQueryFilterGroup)?.Filters;
    if (!Array.isArray(children)) {
      return filter ? [filter] : [];
    }
    return children.reduce((result, child) => result.concat(this.flattenFilters(child)), [] as DwQueryFilter[]);
  }

  private toActiveFilter(config: FilterConfig, filters: DwQueryFilter[]): ActiveFilter {
    if (config.type === FilterType.ReportDatePeriod) {
      const lower = filters.find(filter => filter.Type === DwFilterType.GreaterThanDays
        || filter.Type === DwFilterType.GreaterThanEqualDays
        || filter.Type === DwFilterType.GreaterThan
        || filter.Type === DwFilterType.GreaterThanEqual);
      const upper = filters.find(filter => filter.Type === DwFilterType.LessThanDays
        || filter.Type === DwFilterType.LessThanEqualDays
        || filter.Type === DwFilterType.LessThan
        || filter.Type === DwFilterType.LessThanEqual);
      if (!lower || !upper) {
        return null;
      }
      const dayRelative = lower.Type === DwFilterType.GreaterThanDays
        || lower.Type === DwFilterType.GreaterThanEqualDays;
      const value = dayRelative
        ? { mode: 'rollingDays', days: Math.abs(Number(lower.Value)) }
        : {
          mode: 'exactRange',
          start: this.toDateValue(this.resolveFilterDate(lower)),
          end: this.toDateValue(this.resolveFilterDate(upper, true))
        };
      return {
        field: config.field,
        value,
        displayLabel: config.label,
        displayValue: this.reportDatePeriodDisplay(value)
      };
    }

    if (config.type === FilterType.DateRange) {
      const lower = filters.find(filter => filter.Type === DwFilterType.GreaterThan
        || filter.Type === DwFilterType.GreaterThanEqual
        || filter.Type === DwFilterType.GreaterThanDays
        || filter.Type === DwFilterType.GreaterThanEqualDays);
      const upper = filters.find(filter => filter.Type === DwFilterType.LessThan
        || filter.Type === DwFilterType.LessThanEqual
        || filter.Type === DwFilterType.LessThanDays
        || filter.Type === DwFilterType.LessThanEqualDays);
      if (!lower || !upper) {
        return null;
      }
      const start = this.resolveFilterDate(lower);
      const end = this.resolveFilterDate(upper, true);
      return {
        field: config.field,
        value: { start, end },
        displayLabel: config.label,
        displayValue: `${this.formatDate(start)} - ${this.formatDate(end)}`
      };
    }

    const values = filters.reduce((result, filter) =>
      result.concat(this.getSavedFilterValues(filter)), [] as any[]);
    const value = config.type === FilterType.MultiSelect
      || filters[0].Type === DwFilterType.In
      || filters[0].Type === DwFilterType.NotIn
      ? values
      : values[0];
    return {
      field: config.field,
      value,
      displayLabel: config.label,
      displayValue: this.getConfiguredDefaultDisplayValue(config, value)
    };
  }

  private getSavedFilterValues(filter: DwQueryFilter): any[] {
    const value: any = filter.Value;
    if (Array.isArray(value)) {
      return value;
    }

    // CAP serializes IN/NOT IN arrays as one comma-delimited Value string.
    // Restore the semantic list before resolving lookup displays or rebuilding
    // the editable filter.
    if ((filter.Type === DwFilterType.In || filter.Type === DwFilterType.NotIn)
      && typeof value === 'string') {
      return value.split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }

    return [value];
  }

  private resolveFilterDate(filter: DwQueryFilter, exclusiveUpper = false): Date {
    const isDayRelative = filter.Type === DwFilterType.GreaterThanDays
      || filter.Type === DwFilterType.GreaterThanEqualDays
      || filter.Type === DwFilterType.LessThanDays
      || filter.Type === DwFilterType.LessThanEqualDays;
    if (isDayRelative) {
      const result = new Date();
      result.setHours(0, 0, 0, 0);
      result.setDate(result.getDate() + Number(filter.Value || 0));
      return result;
    }
    const result = new Date(filter.Value as any);
    if (exclusiveUpper && filter.Type === DwFilterType.LessThan) {
      result.setDate(result.getDate() - 1);
    }
    return result;
  }

  private applyDefaultLookupFilters(generation: number): void {
    const defaultConfigs = this.filterConfigs.filter(config =>
      config.defaultToFirstLookupOption && !!config.lookupId
    );

    if (!defaultConfigs.length) {
      return;
    }

    forkJoin(defaultConfigs.map(config =>
      this.getLookupQueryParams(config).pipe(
        switchMap(queryParams => this.dwMetaDataService.getLookup(config.lookupId, queryParams)),
        map(items => ({ config, item: items?.[0] || null })),
        catchError(() => of({ config, item: null }))
      )
    )).subscribe(defaults => {
      if (generation !== this.appliedFilterGeneration) {
        return;
      }

      let defaultsApplied = false;

      defaults.forEach(result => {
        if (!result.item || this.activeFilters.some(filter => filter.field === result.config.field)) {
          return;
        }

        this.activeFilters.push({
          field: result.config.field,
          value: result.item.Id,
          displayLabel: result.config.label,
          displayValue: result.item.Display || result.item.Id
        });
        defaultsApplied = true;
      });

      if (defaultsApplied) {
        this.updateAvailableFilters();
        this.emitFiltersChanged();
      }
    });
  }

  /**
   * Activates explicitly configured defaults when the filter component loads.
   * Lookup-backed defaults resolve their display labels before the filters are emitted.
   */
  private applyConfiguredDefaultFilters(): void {
    const generation = ++this.appliedFilterGeneration;
    this.activeFilters = [];
    this.updateAvailableFilters();
    const defaultConfigs = this.filterConfigs.filter(config => this.hasConfiguredDefault(config.defaultValue));

    if (!defaultConfigs.length) {
      this.applyDefaultLookupFilters(generation);
      return;
    }

    forkJoin(defaultConfigs.map(config => {
      const value = config.defaultValue;

      if ((config.type === FilterType.Dropdown || config.type === FilterType.MultiSelect) && config.lookupId) {
        return this.resolveLookupDisplayValue(config, value).pipe(
          map(displayValue => ({
            config,
            value,
            displayValue
          }))
        );
      }

      return of({
        config,
        value,
        displayValue: this.getConfiguredDefaultDisplayValue(config, value)
      });
    })).subscribe(defaults => {
      if (generation !== this.appliedFilterGeneration) {
        return;
      }

      this.activeFilters = defaults.map(result => ({
        field: result.config.field,
        value: result.value,
        displayLabel: result.config.label,
        displayValue: result.displayValue
      }));

      this.updateAvailableFilters();
      this.emitFiltersChanged();
      this.applyDefaultLookupFilters(generation);
    });
  }

  private hasConfiguredDefault(value: any): boolean {
    return value !== undefined
      && value !== null
      && value !== ''
      && (!Array.isArray(value) || value.length > 0);
  }

  private getConfiguredDefaultDisplayValue(config: FilterConfig, value: any): string {
    switch (config.type) {
      case FilterType.DateRange:
        return value?.start && value?.end
          ? `${this.formatDate(value.start)} - ${this.formatDate(value.end)}`
          : '';
      case FilterType.ReportDatePeriod:
        return this.reportDatePeriodDisplay(value);
      case FilterType.DatePicker:
        return this.formatDate(value);
      case FilterType.Toggle:
        return value ? 'Yes' : 'No';
      case FilterType.Dropdown:
      case FilterType.MultiSelect:
        return this.getDisplayValueForDropdown(value, config);
      default:
        return `${value}`;
    }
  }

  private resolveLookupDisplayValue(config: FilterConfig, value: any, loadedOptions: any[] = []) {
    return this.getLookupQueryParams(config).pipe(
      switchMap(queryParams => this.lookupDisplayResolver.resolve(
        config.lookupId,
        value,
        queryParams,
        loadedOptions
      ))
    );
  }

  private getLookupQueryParams(config: FilterConfig): Observable<DwQueryParam[]> {
    const explicitValues = config.lookupParams || [];
    if (config.lookupParamNames?.length) {
      return of(this.buildLookupQueryParams(config.lookupParamNames, explicitValues));
    }

    return this.dwMetaDataService.getLookupDefinition(config.lookupId).pipe(
      take(1),
      map(lookupDefinition => {
        const parameterDefinitions = lookupDefinition?.DwMetaDataLookupParam || [];
        const names = parameterDefinitions
          .map(parameter => parameter.ParamNameOverride || parameter.AttributeName)
          .filter(name => !!name);
        const defaultValues = parameterDefinitions.map(parameter => parameter.DefaultValue);

        if (!names.length && explicitValues.length === 1) {
          return this.buildLookupQueryParams(['FlightSchoolOrganizationId'], explicitValues);
        }

        return this.buildLookupQueryParams(names, explicitValues, defaultValues);
      }),
      catchError(error => {
        console.warn(`Lookup parameter metadata could not be loaded for ${config.lookupId}.`, error);
        return of([] as DwQueryParam[]);
      })
    );
  }

  private buildLookupQueryParams(
    names: string[],
    explicitValues: any[],
    defaultValues: any[] = []
  ): DwQueryParam[] {
    return names
      .map((name, index) => {
        const contextValue = this.getLookupContextValue(name);
        return {
          name,
          value: explicitValues[index] !== undefined
            ? explicitValues[index]
            : contextValue !== undefined
              ? contextValue
              : defaultValues[index]
        };
      })
      .filter(parameter => parameter.value !== null && parameter.value !== undefined && parameter.value !== '')
      .map(parameter => ({
        ParamName: parameter.name,
        ParamValue: `${parameter.value}`
      }));
  }

  private getLookupContextValue(paramName: string): any {
    if (!paramName || !this.lookupContext) {
      return undefined;
    }

    const propertyName = `${paramName.charAt(0).toLowerCase()}${paramName.slice(1)}`;
    return this.lookupContext[propertyName] !== undefined
      ? this.lookupContext[propertyName]
      : this.lookupContext[paramName];
  }

  private buildConfiguredFilter(
    queryHelper: DwQueryHelper,
    config: FilterConfig,
    attrName: string,
    value: any,
    defaultOperator: DwFilterType
  ) {
    const operator = config.operator || defaultOperator;
    const scalarValue = Array.isArray(value) ? value[0] : value;
    switch (operator) {
      case DwFilterType.NotEqual: return queryHelper.filters.NotEqual(attrName, scalarValue);
      case DwFilterType.In: return queryHelper.filters.InList(attrName, ensureArray(value));
      case DwFilterType.NotIn: return queryHelper.filters.NotInList(attrName, ensureArray(value));
      case DwFilterType.Contains: return queryHelper.filters.Contains(attrName, scalarValue);
      case DwFilterType.StartsWith: return queryHelper.filters.StartsWith(attrName, scalarValue);
      case DwFilterType.EndsWith: return queryHelper.filters.EndsWith(attrName, scalarValue);
      case DwFilterType.GreaterThanEqual: return queryHelper.filters.GreaterThanEqual(attrName, scalarValue);
      case DwFilterType.LessThanEqual: return queryHelper.filters.LessThanEqual(attrName, scalarValue);
      default: return queryHelper.filters.Equal(attrName, scalarValue);
    }
  }

  /**
   * Gets display value for dropdown/multi-select options
   */
  private getDisplayValueForDropdown(value: any, config: FilterConfig): string {
    if (!value) {
      return '';
    }

    const values = ensureArray(value);
    
    if (config.options) {
      const opts = config.options || [];
      const labels = values.map(v => {
        const option = opts.find(o => `${o.value}` === `${v}`);
        return option ? option.label : v;
      });
      return labels.join(', ');
    }
    // Lookup-backed dropdowns require async resolution from the configured lookup list.
    // The caller handles that resolution; just return raw values here.
    return values.join(', ');
  }

  /**
   * Formats a date for display
   */
  private formatDate(date: Date | string): string {
    if (!date) {
      return '';
    }
    
    const d = this.toLocalDate(date);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  }

  /**
   * Resets temporary filter values
   */
  private resetTempValues(): void {
    this.tempFilterValue = null;
    this.tempDateRangeStart = null;
    this.tempDateRangeEnd = null;
    this.tempReportDateMode = 'rollingDays';
    this.tempReportDateDays = 90;
  }

  private buildReportDatePeriodFilter(qh: DwQueryHelper, config: FilterConfig, value: any): DwQueryFilterGroup {
    const attr = typeof config.attribute === 'string' ? config.attribute : config.attribute.fieldNameWithItemPrefix;
    const group = new DwQueryFilterGroup();
    if (value.mode === 'rollingDays') {
      group.AddFilterAnd(qh.filters.DateFilters().GreaterThanEqualDays(attr, -Number(value.days)));
      group.AddFilterAnd(qh.filters.DateFilters().LessThanEqualDays(attr, 0));
      return group;
    }
    const range = this.resolveReportDateRange(value);
    group.AddFilterAnd(qh.filters.GreaterThanEqual(attr, range.start));
    group.AddFilterAnd(qh.filters.LessThan(attr, range.endExclusive));
    return group;
  }

  private resolveReportDateRange(value: any): { start: Date; endExclusive: Date } {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const quarterMonth = Math.floor(month / 3) * 3;
    switch (value.mode) {
      case 'currentMonth': return { start: new Date(year, month, 1), endExclusive: new Date(year, month + 1, 1) };
      case 'previousMonth': return { start: new Date(year, month - 1, 1), endExclusive: new Date(year, month, 1) };
      case 'currentQuarter': return { start: new Date(year, quarterMonth, 1), endExclusive: new Date(year, quarterMonth + 3, 1) };
      case 'previousQuarter': return { start: new Date(year, quarterMonth - 3, 1), endExclusive: new Date(year, quarterMonth, 1) };
      case 'currentYear': return { start: new Date(year, 0, 1), endExclusive: new Date(year + 1, 0, 1) };
      case 'previousYear': return { start: new Date(year - 1, 0, 1), endExclusive: new Date(year, 0, 1) };
      case 'exactRange': {
        const start = new Date(`${value.start}T00:00:00`);
        const endExclusive = new Date(`${value.end}T00:00:00`);
        endExclusive.setDate(endExclusive.getDate() + 1);
        return { start, endExclusive };
      }
      default: throw new Error(`Unsupported report date period: ${value.mode}`);
    }
  }

  private reportDatePeriodDisplay(value: any): string {
    switch (value?.mode) {
      case 'rollingDays': return `Last ${value.days} days`;
      case 'currentMonth': return 'Current month';
      case 'previousMonth': return 'Previous month';
      case 'currentQuarter': return 'Current quarter';
      case 'previousQuarter': return 'Previous quarter';
      case 'currentYear': return 'Current year';
      case 'previousYear': return 'Previous year';
      case 'exactRange': return `${value.start} - ${value.end}`;
      default: return 'Reporting period';
    }
  }

  private toLocalDate(value: Date | string): Date {
    // Date-only ISO strings must stay on the selected local calendar day.
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
  }

  private toDateValue(value: Date | string): string {
    const date = this.toLocalDate(value);

    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
